import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';

import { filter, take } from 'rxjs';

import { ARGENTUS_CONTACT_INFO } from '@constants/constants';
import { FOOTER_TEXTS } from '@constants/text.constants';

interface FooterNavItem {
  label: string;
  link: string;
  action?: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css',
})
export class FooterComponent {
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  public readonly currentYear = new Date().getFullYear();
  public readonly texts = FOOTER_TEXTS;
  public readonly ARGENTUS_CONTACT_INFO = ARGENTUS_CONTACT_INFO;

  public readonly navItems: FooterNavItem[] =
    this.texts.NAV.ITEMS.filter((item: FooterNavItem) => {
      const hiddenItems = [
        'preguntas frecuentes',
        'envios y entregas',
      ];

      return !hiddenItems.includes(
        this.normalizeText(item.label),
      );
    });

  public readonly normalizedPhone =
    this.texts.CONTACT.PHONE.replace(/[^\d+]/g, '');

  public onNavItemClick(item: FooterNavItem): void {
    if (item.action !== 'scroll-to-top') {
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0];
    const destinationUrl = item.link.split('?')[0];

    if (currentUrl === destinationUrl) {
      this.scrollToTop();
      return;
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd =>
            event instanceof NavigationEnd,
        ),
        take(1),
      )
      .subscribe(() => {
        this.scrollToTop();
      });
  }

  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  private normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }
}
